/* eslint-disable react/prop-types */
import React, { memo, useState } from 'react';
import { TouchableOpacity, View, Alert, Platform } from 'react-native';
import { QText } from './index';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '../hooks/MakeStylesHook';
import FileIcon from './Icons/FileIcon';
import UniversalFileViewer from './UniversalFileViewer';
import UniversalAttachmentPreview from './UniversalAttachmentPreview';
import SecureFileOpener from './SecureFileOpener';

const useStyles = makeStyles((theme) => ({
  container: {
    marginVertical: 10,
  },
  labelHeading: {
    color: theme.colors.secondaryText,
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  fileList: {
    marginTop: 5,
  },
  fileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: theme.colors.outline,
  },
  fileInfo: {
    flex: 1,
    marginLeft: 10,
  },
  fileName: {
    color: theme.colors.secondaryText,
    fontSize: 14,
    fontWeight: '500',
  },
  fileSize: {
    color: theme.colors.secondaryText,
    fontSize: 12,
    opacity: 0.7,
    marginTop: 2,
  },
  emptyState: {
    padding: 15,
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.outline,
    borderStyle: 'dashed',
  },
  emptyText: {
    color: theme.colors.secondaryText,
    fontSize: 14,
    opacity: 0.7,
    textAlign: 'center',
  },
}));

const AttachmentDisplay = ({
  attachments = [],
  title = 'Attachments',
  queueUserId,
}) => {
  const styles = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [secureFileRequest, setSecureFileRequest] = useState(null);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Size unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFilePreview = (file) => {
    setCurrentFile(file);
    setShowFileViewer(true);
  };

  const openFile = async (file) => {
    // With the new simplified approach, we just need the attachment ID
    const attachmentId = file?.id;

    if (!attachmentId) {
      Alert.alert(
        'Cannot Open File',
        'File ID is missing. Please try refreshing the page.',
        [{ text: 'OK' }]
      );
      return;
    }

    try {
      // Set the secure file request with just the attachment ID
      setSecureFileRequest({ attachmentId });
    } catch (error) {
      console.error('Error opening secure file:', error);
      Alert.alert(
        'Error Opening File',
        'Failed to open the file. Please try again or contact support if the issue persists.',
        [{ text: 'OK' }]
      );
    }
  };

  if (!attachments || attachments.length === 0) {
    return (
      <View style={styles.container}>
        <QText style={styles.labelHeading}>
          {t(title.toLowerCase()) || title}
        </QText>
        <View style={styles.emptyState}>
          <QText style={styles.emptyText}>
            {t('no_attachments') || 'No attachments available'}
          </QText>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <QText style={styles.labelHeading}>
        {t(title.toLowerCase()) || title}
      </QText>

      <View style={styles.fileList}>
        {attachments.map((file, index) => {
          return (
            <TouchableOpacity
              key={file.id || index}
              style={styles.fileItem}
              onPress={() => handleFilePreview(file)}
            >
              <UniversalAttachmentPreview
                attachment={file}
                showExtension={false}
                style={{ width: 24, height: 24 }}
              />
              <View style={styles.fileInfo}>
                <QText style={styles.fileName} numberOfLines={1}>
                  {file.name || `Attachment ${index + 1}`}
                </QText>
                <QText style={styles.fileSize}>
                  {formatFileSize(file.size)}
                </QText>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Universal File Viewer */}
      <UniversalFileViewer
        visible={showFileViewer}
        onClose={() => setShowFileViewer(false)}
        attachment={currentFile}
      />

      {/* Secure File Opener */}
      {secureFileRequest && (
        <SecureFileOpener
          attachmentId={secureFileRequest.attachmentId}
          onComplete={() => setSecureFileRequest(null)}
        />
      )}
    </View>
  );
};

export default memo(AttachmentDisplay);
